import { isEqualTime } from "@suse-o11y/date-time";
import type { IdOrIdentifier } from "@suse-o11y/id-or-identifier";
import { matchIdentifiable } from "@suse-o11y/identifiable";
import { isArrayEqual } from "@suse-o11y/is-array-equal";
import { map } from "@suse-o11y/map";
import { not } from "@suse-o11y/not";
import { piped } from "@suse-o11y/piped";
import { prop } from "@suse-o11y/prop";
import { sortBy } from "@suse-o11y/sort-by";
import { toKeys } from "@suse-o11y/to-keys";

import { DashboardSpec } from "../common/dashboard-spec";
import {
	type DashboardReadBaseSchema,
	type DashboardReadMetadataSchema,
	type DashboardCloneSchema as OpenApiDashboardCloneSchema,
	type DashboardPatchSchema as OpenApiDashboardPatchSchema,
	type DashboardReadFullSchema as OpenApiDashboardReadFullSchema,
	type DashboardWriteSchema as OpenApiDashboardWriteSchema,
	cloneDashboardFn,
	createDashboardFn,
	deleteDashboardFn,
	getDashboardFn,
	getDashboardsFn,
	patchDashboardFn,
} from "../common/openapi";

import {
	type OptimisticUpdate,
	type QueryCacheInteractions,
	type UseFullQueryOptions,
	type UseMutationOptions,
	type UseMutationResult,
	optimisticQueryUpdate,
	useMutation,
	useQueryClient,
} from "./react-query";

function isDashboardEqual(left: DashboardReadBaseSchema, right: DashboardReadBaseSchema) {
	return left.id === right.id && isEqualTime(left.lastUpdateTimestamp, right.lastUpdateTimestamp);
}

const QUERY_ALL_DASHBOARDS_QUERY_KEY = ["dashboards", "all", "meta"] as const;

async function queryAllDashboardsQueryFn() {
	const { dashboards } = await getDashboardsFn({ loadFullDashboard: false });

	// the endpoint returns DashboardReadMetadataSchema when loadFullDashboard is false
	// but the type is not inferred correctly
	return dashboards as ReadonlyArray<DashboardReadMetadataSchema>;
}

export const queryAllDashboards = {
	queryKey: QUERY_ALL_DASHBOARDS_QUERY_KEY,
	queryFn: queryAllDashboardsQueryFn,

	isDataEqual(
		oldData: undefined | ReadonlyArray<DashboardReadMetadataSchema>,
		newData: ReadonlyArray<DashboardReadMetadataSchema>,
	) {
		return isArrayEqual(oldData ?? [], newData, isDashboardEqual);
	},

	// TODO https://stackstate.atlassian.net/browse/OBSFE-120
	// it is ugly to have the query key like that, but it is the only way to make it work
	prefetchData: (queryClient) =>
		queryClient.prefetchQuery([{}, ...QUERY_ALL_DASHBOARDS_QUERY_KEY], queryAllDashboardsQueryFn, {
			staleTime: Number.POSITIVE_INFINITY,
		}),
	getData: (queryClient) => queryClient.getQueryData([{}, ...QUERY_ALL_DASHBOARDS_QUERY_KEY]),
	setData: (queryClient, data) => queryClient.setQueryData([{}, ...QUERY_ALL_DASHBOARDS_QUERY_KEY], data),
	removeData: (queryClient) => queryClient.removeQueries([{}, ...QUERY_ALL_DASHBOARDS_QUERY_KEY]),
	invalidate: (queryClient) => queryClient.invalidateQueries([{}, ...QUERY_ALL_DASHBOARDS_QUERY_KEY]),
	cancel: (queryClient) => queryClient.cancelQueries([{}, ...QUERY_ALL_DASHBOARDS_QUERY_KEY]),
	reset: (queryClient) => queryClient.resetQueries([{}, ...QUERY_ALL_DASHBOARDS_QUERY_KEY]),
} satisfies UseFullQueryOptions & QueryCacheInteractions<ReadonlyArray<DashboardReadMetadataSchema>>;

export interface DashboardReadFullSchema extends Omit<OpenApiDashboardReadFullSchema, "dashboard"> {
	spec: DashboardSpec;
}

function dashboardReadFullSchemaFromOpenApi(schema: OpenApiDashboardReadFullSchema): DashboardReadFullSchema {
	return {
		...schema,
		spec: DashboardSpec.fromPerses(schema.dashboard),
	};
}

const QUERY_ALL_FULL_DASHBOARDS_QUERY_KEY = ["dashboards", "all", "full"] as const;

export const queryAllFullDashboards = {
	queryKey: QUERY_ALL_FULL_DASHBOARDS_QUERY_KEY,
	queryFn: async () => {
		const { dashboards } = await getDashboardsFn({ loadFullDashboard: true });

		// the endpoint returns DashboardReadFullSchema when loadFullDashboard is true
		// but the type is not inferred correctly
		return (dashboards as ReadonlyArray<OpenApiDashboardReadFullSchema>).map(dashboardReadFullSchemaFromOpenApi);
	},

	isDataEqual(
		oldData: undefined | ReadonlyArray<DashboardReadFullSchema>,
		newData: ReadonlyArray<DashboardReadFullSchema>,
	) {
		return isArrayEqual(oldData ?? [], newData, isDashboardEqual);
	},

	// TODO https://stackstate.atlassian.net/browse/OBSFE-120
	// it is ugly to have the query key like that, but it is the only way to make it work
	getData: (queryClient) => queryClient.getQueryData([{}, ...QUERY_ALL_FULL_DASHBOARDS_QUERY_KEY]),
	setData: (queryClient, data) => queryClient.setQueryData([{}, ...QUERY_ALL_FULL_DASHBOARDS_QUERY_KEY], data),
	removeData: (queryClient) => queryClient.removeQueries([{}, ...QUERY_ALL_FULL_DASHBOARDS_QUERY_KEY]),
	invalidate: (queryClient) => queryClient.invalidateQueries([{}, ...QUERY_ALL_FULL_DASHBOARDS_QUERY_KEY]),
	cancel: (queryClient) => queryClient.cancelQueries([{}, ...QUERY_ALL_FULL_DASHBOARDS_QUERY_KEY]),
	reset: (queryClient) => queryClient.resetQueries([{}, ...QUERY_ALL_FULL_DASHBOARDS_QUERY_KEY]),
} satisfies UseFullQueryOptions & QueryCacheInteractions<ReadonlyArray<DashboardReadFullSchema>>;

export const queryDashboard = ((idOrIdentifier) => {
	const queryKey = ["dashboards", idOrIdentifier.toString(), "meta"] as const;

	return {
		queryKey,
		queryFn: async () => {
			const dashboard = await getDashboardFn({
				dashboardIdOrUrn: idOrIdentifier.toString(),
				loadFullDashboard: false,
			});

			// the endpoint returns DashboardReadMetadataSchema when loadFullDashboard is false
			// but the type is not inferred correctly
			return dashboard as DashboardReadMetadataSchema;
		},

		isDataEqual(oldData: undefined | DashboardReadMetadataSchema, newData: DashboardReadMetadataSchema) {
			return oldData != null && isDashboardEqual(oldData, newData);
		},

		// TODO https://stackstate.atlassian.net/browse/OBSFE-120
		// it is ugly to have the query key like that, but it is the only way to make it work
		getData: (queryClient) => queryClient.getQueryData([{}, ...queryKey]),
		setData: (queryClient, data) => queryClient.setQueryData([{}, ...queryKey], data),
		removeData: (queryClient) => queryClient.removeQueries([{}, ...queryKey]),
		invalidate: (queryClient) => queryClient.invalidateQueries([{}, ...queryKey]),
		cancel: (queryClient) => queryClient.cancelQueries([{}, ...queryKey]),
		reset: (queryClient) => queryClient.resetQueries([{}, ...queryKey]),
	};
}) satisfies (
	idOrIdentifier: IdOrIdentifier,
) => UseFullQueryOptions & QueryCacheInteractions<DashboardReadMetadataSchema>;

export const queryFullDashboard = ((idOrIdentifier) => {
	const queryKey = ["dashboards", idOrIdentifier.toString(), "full"] as const;

	return {
		queryKey,
		queryFn: async () => {
			const dashboard = await getDashboardFn({
				dashboardIdOrUrn: idOrIdentifier.toString(),
				loadFullDashboard: true,
			});

			// the endpoint returns DashboardReadFullSchema when loadFullDashboard is true
			// but the type is not inferred correctly
			return dashboardReadFullSchemaFromOpenApi(dashboard as OpenApiDashboardReadFullSchema);
		},

		structuralSharing: false,

		isDataEqual(oldData: undefined | DashboardReadFullSchema, newData: DashboardReadFullSchema) {
			return oldData != null && isDashboardEqual(oldData, newData);
		},

		// TODO https://stackstate.atlassian.net/browse/OBSFE-120
		// it is ugly to have the query key like that, but it is the only way to make it work
		getData: (queryClient) => queryClient.getQueryData([{}, ...queryKey]),
		setData: (queryClient, data) => queryClient.setQueryData([{}, ...queryKey], data),
		removeData: (queryClient) => queryClient.removeQueries([{}, ...queryKey]),
		invalidate: (queryClient) => queryClient.invalidateQueries([{}, ...queryKey]),
		cancel: (queryClient) => queryClient.cancelQueries([{}, ...queryKey]),
		reset: (queryClient) => queryClient.resetQueries([{}, ...queryKey]),
	};
}) satisfies (idOrIdentifier: IdOrIdentifier) => UseFullQueryOptions & QueryCacheInteractions<DashboardReadFullSchema>;

export interface DashboardWriteSchema extends Omit<OpenApiDashboardWriteSchema, "dashboard"> {
	spec: DashboardSpec;
}

function dashboardWriteSchemaToOpenApi(schema: DashboardWriteSchema): OpenApiDashboardWriteSchema {
	return {
		...schema,
		dashboard: schema.spec.toPerses(),
	};
}

export const createDashboard = {
	mutationKey: ["dashboards", "create"],
	mutationFn: async (payload) => {
		const dashboard = await createDashboardFn({ body: dashboardWriteSchemaToOpenApi(payload) });

		return dashboardReadFullSchemaFromOpenApi(dashboard);
	},
} satisfies UseMutationOptions<DashboardReadFullSchema, Error, DashboardWriteSchema>;

export interface DashboardCloneSchema extends Omit<OpenApiDashboardCloneSchema, "dashboard"> {
	spec?: DashboardSpec;
}

function dashboardCloneSchemaToOpenApi(schema: DashboardCloneSchema): OpenApiDashboardCloneSchema {
	return {
		...schema,
		dashboard: schema.spec?.toPerses(),
	};
}

export interface CloneDashboardParams {
	idOrIdentifier: IdOrIdentifier;
	diff: DashboardCloneSchema;
}

export const cloneDashboard = {
	mutationKey: ["dashboards", "clone"],
	mutationFn: async ({ idOrIdentifier, diff }) => {
		const dashboard = await cloneDashboardFn({
			dashboardIdOrUrn: idOrIdentifier.toString(),
			body: dashboardCloneSchemaToOpenApi(diff),
		});

		return dashboardReadFullSchemaFromOpenApi(dashboard);
	},
} satisfies UseMutationOptions<DashboardReadFullSchema, Error, CloneDashboardParams>;

export interface DashboardPatchSchema extends Omit<OpenApiDashboardPatchSchema, "dashboard"> {
	spec?: DashboardSpec;
}

function dashboardPatchSchemaToOpenApi({ spec, ...schema }: DashboardPatchSchema): OpenApiDashboardPatchSchema {
	return {
		...schema,
		dashboard: spec?.toPerses(),
	};
}

export interface UpdateDashboardParams {
	idOrIdentifier: IdOrIdentifier;
	diff: DashboardPatchSchema;
}

export const updateDashboard = {
	mutationKey: ["dashboards", "update"],
	mutationFn: async ({ idOrIdentifier, diff }) => {
		const dashboard = await patchDashboardFn({
			dashboardIdOrUrn: idOrIdentifier.toString(),
			body: dashboardPatchSchemaToOpenApi(diff),
		});

		return dashboardReadFullSchemaFromOpenApi(dashboard);
	},
} satisfies UseMutationOptions<DashboardReadFullSchema, Error, UpdateDashboardParams>;

export interface UseOptimisticUpdateDashboardOptionsContext {
	meta: ReadonlyArray<undefined | OptimisticUpdate<unknown>>;
	full: ReadonlyArray<undefined | OptimisticUpdate<unknown>>;
}

export type UseOptimisticUpdateDashboardOptions = Omit<
	UseMutationOptions<DashboardReadFullSchema, Error, UpdateDashboardParams, UseOptimisticUpdateDashboardOptionsContext>,
	keyof typeof updateDashboard
>;

export const useOptimisticUpdateDashboard = ({
	onSuccess,
	onError,
	...options
}: UseOptimisticUpdateDashboardOptions = {}): UseMutationResult<
	DashboardReadFullSchema,
	Error,
	UpdateDashboardParams
> => {
	const queryClient = useQueryClient();

	return useMutation({
		...updateDashboard,
		...options,

		onMutate: async ({ idOrIdentifier, diff }) => {
			const lastUpdateTimestamp = Date.now();

			const meta = [
				optimisticQueryUpdate(queryClient, queryDashboard(idOrIdentifier), (dashboard) => ({
					...dashboard,
					...diff,
					lastUpdateTimestamp,
				})),

				optimisticQueryUpdate(
					queryClient,
					queryAllDashboards,
					piped(
						map((dashboard) =>
							matchIdentifiable(dashboard, idOrIdentifier) ? { ...dashboard, ...diff, lastUpdateTimestamp } : dashboard,
						),
						sortBy(prop("name")),
					),
				),
			];

			const full = [
				optimisticQueryUpdate(
					queryClient,
					queryAllFullDashboards,
					piped(
						map((dashboard) =>
							matchIdentifiable(dashboard, idOrIdentifier) ? { ...dashboard, ...diff, lastUpdateTimestamp } : dashboard,
						),
						sortBy(prop("name")),
					),
				),

				optimisticQueryUpdate(queryClient, queryFullDashboard(idOrIdentifier), (dashboard) => ({
					...dashboard,
					...diff,
					lastUpdateTimestamp,
				})),
			];

			// cancel outgoing queries so they do not override optimistic update
			await Promise.all([...meta, ...full].map((entry) => entry?.cancelOngoingQuery()));

			return { meta, full };
		},

		onSuccess: (data, variables, context) => {
			const isDashboardOnly = toKeys(variables.diff).length === 1 && variables.diff.spec != null;
			// invalidate the meta when the diff is not only the dashboard
			const updates = isDashboardOnly ? context.meta : [...context.meta, ...context.full];

			return Promise.all([
				onSuccess?.(data, variables, context),

				Promise.all(updates.map((entry) => entry?.invalidateOptimisticData())),
			]);
		},

		onError: (error, variables, context = { meta: [], full: [] }) => {
			for (const entry of [...context.meta, ...context.full]) {
				entry?.rollbackOptimisticUpdates();
			}

			return onError?.(error, variables, context);
		},
	});
};

export const deleteDashboard = {
	mutationKey: ["dashboards", "delete"],
	mutationFn: async (idOrIdentifier) => deleteDashboardFn({ dashboardIdOrUrn: idOrIdentifier.toString() }),
} satisfies UseMutationOptions<void, Error, number | string>;

export type UseOptimisticDeleteDashboardOptionsContext = ReadonlyArray<undefined | OptimisticUpdate<unknown>>;

export type UseOptimisticDeleteDashboardOptions = Omit<
	UseMutationOptions<void, Error, number | string, UseOptimisticDeleteDashboardOptionsContext>,
	keyof typeof deleteDashboard
>;

export const useOptimisticDeleteDashboard = ({
	onSuccess,
	onError,
	...options
}: UseOptimisticDeleteDashboardOptions = {}): UseMutationResult<void, Error, number | string> => {
	const queryClient = useQueryClient();

	return useMutation({
		...deleteDashboard,
		...options,
		onMutate: async (idOrIdentifier) => {
			const updates = [
				optimisticQueryUpdate(queryClient, queryDashboard(idOrIdentifier), undefined),
				optimisticQueryUpdate(queryClient, queryFullDashboard(idOrIdentifier), undefined),
				optimisticQueryUpdate(queryClient, queryAllDashboards, (dashboards) =>
					dashboards.filter(not(matchIdentifiable(idOrIdentifier))),
				),
				optimisticQueryUpdate(queryClient, queryAllFullDashboards, (dashboards) =>
					dashboards.filter(not(matchIdentifiable(idOrIdentifier))),
				),
			];

			// cancel outgoing queries so they do not override optimistic update
			await Promise.all(updates.map((entry) => entry?.cancelOngoingQuery()));

			return updates;
		},
		onSuccess: (data, variables, context) => {
			return Promise.all([
				onSuccess?.(data, variables, context),
				// trigger a refetch of all dashboards
				context.map((entry) => entry?.invalidateOptimisticData()),
			]);
		},
		onError: (error, variables, context = []) => {
			for (const entry of context) {
				entry?.rollbackOptimisticUpdates();
			}

			return onError?.(error, variables, context);
		},
	});
};
